import { trpc } from '@/main'
import { useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { deriveProfiles, useRootSearch } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

/**
 * Loader-side prefetch of the PERSISTENT job chrome (JobsLayout integrants panel + breadcrumb): the
 * lightweight `getJob` detail and the integrants-infinite list. This lives in the `jobs.$jobId` layout
 * loader — that route renders real content (the incomplete-job auto-open + the tab Outlet), so per
 * AGENTS.md it owns the jobId-keyed chrome its subtree reads (the tab leaves no longer duplicate it).
 * All deferred: the panel reads these via suspense.
 */
export type JobChromeSearchDeps = Pick<RootSearch, 'first' | 'second' | 'type' | 'o' | 'od'>

/**
 * `loaderDeps` picker for the `jobs.$jobId` layout: EXACTLY the search keys `prefetchJobChrome`
 * consumes (profile derivation + the integrants list), so the router's deps memoization re-runs the
 * chrome loader only when those can change — not on unrelated search churn (dialog ids, list sorts).
 */
export function jobChromeSearchDeps(search: JobChromeSearchDeps): JobChromeSearchDeps {
  return { first: search.first, second: search.second, type: search.type, o: search.o, od: search.od }
}

export function prefetchJobChrome(
  queryClient: QueryClient,
  search: JobChromeSearchDeps,
  params: Parameters<typeof deriveProfiles>[1],
) {
  const { first } = deriveProfiles(search, params, false)
  const source =
    first?.type === 'job' && 'source' in first && first.source === 'integrants' ? 'integrants' : 'described'
  queryClient.prefetchQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(params.jobId) }))
  queryClient.prefetchInfiniteQuery(
    trpc.jobs.getJobIntegrants.infiniteQueryOptions(
      {
        jobId: Number(params.jobId),
        type: search.type,
        o: search.o,
        od: search.od,
        q: '',
        employeesOnly: true,
        source,
      },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
  )
}

/**
 * Suspense data hook for the job board/distribution subviews (its only consumers). Reads the
 * integrants indicators via `useSuspenseQuery` so the subview's <Suspense fallback> owns the loading
 * skeleton (board goes skeleton → real, never a flash of empty board). `type` is deferred so changing
 * the type filter keeps the PREVIOUS board on screen while the new data loads (keep-previous UX,
 * mirroring the report). Requires a concrete `jobId` — the guard component handles the no-job case
 * before mounting this hook (useSuspenseQuery can't be disabled with skipToken).
 */
export function useJob(jobId: number) {
  const { type } = useRootSearch()
  const deferredType = useDeferredValue(type)
  const { data: jobIntegrants, isFetching } = useSuspenseQuery(
    trpc.companies.getJobIntegrantIndicators.queryOptions({ jobId, type: deferredType }),
  )
  return { jobIntegrants, isFetching }
}
